import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';

import {
  MODAL_RELATION_LIST,
  WELLNESS_COVERAGE_LIMIT_FPI,
  WELLNESS_COVERAGE_LIMIT_IPG,
  WELLNESS_MULTIMODAL_ID,
} from '../../../../constants';

import styles from './WellnessCoverage.module.css';

import infoIcon from '../../../../assets/changeCoverage/plans/info.svg';
import checkmark from '../../../../assets/changeCoverage/plans/checkmark.svg';

import { Tooltip } from './Tooltip';
import { TabList } from './Tablist';

import { currencyFormat } from '../../../../services/utils';

import { prop103 } from '../changeCoverageOptions/prop103-data';
import { usePetcloudCCProp103 } from '../../../../hooks';

const { WELLNESS_MODAL_ID } = MODAL_RELATION_LIST;

/** Multimodal structure needs to be handled for this specific scenario */
const restructureList = (multimodalStructure, petType) => {
  let mainItems = [];

  multimodalStructure?.InsuranceMultiModalItemsDtoList
    .forEach((currentItem) => {
      if (currentItem.PetType !== petType) {
        return;
      }

      if (!currentItem.OrderParentId) {
        mainItems = mainItems.concat({
          ...currentItem,
          tooltipItems: [],
        });
      } else {
        const prevItem = mainItems
          .find((item) => item.OrderId === currentItem.OrderParentId);
        if (prevItem) {
          prevItem.tooltipItems = prevItem.tooltipItems.concat(currentItem);
        }
      }
    });

  return mainItems;
};

const TabCoverage = ({ info }) => (
  <>
    {info.IsSelected && (
      <img alt="" className={styles.checkmark} src={checkmark} />
    )}

    <span>
      {info.TitleText}: {currencyFormat({
        value: info.FeeAmount,
      })}
    </span>
  </>
);

const Wrapper = ({
  isListItem = false,
  children,
  className = '',
}) => (isListItem ? (
  <li className={className}>{children}</li>
) : <div className={className}>{children}</div>);

const CoverageItem = ({
  image = '',
  isBold = false,
  isListItem = false,
  label = '',
  tooltipItems = [],
  value = '',
}) => {
  const { t } = useTranslation('changeCoverage');

  return (
    <Wrapper
      className={`${styles.item} ${isBold ? styles.bold : ''}`}
      isListItem={isListItem}
    >
      <span>
        {image && <img alt="" className={styles.image} src={image} />}

        <span>{label}</span>

        {!!tooltipItems.length && (
          <Tooltip
            content={(
              <ul className={styles.tooltipList}>
                {tooltipItems.map((item) => (
                  <li key={item.Id}>{item.ItemLabel}</li>
                ))}
              </ul>
            )}
            tooltipClassName={styles.tooltip}
          >
            {/* Needs an interactive element */}
            <button className={styles.buttonTooltip} type="button">
              <img alt={t('powerUpsSection.info')} src={infoIcon} />
            </button>
          </Tooltip>
        )}
      </span>

      <span>{value}</span>
    </Wrapper>
  );
};

export const WellnessCoverage = ({
  bundleSelected,
  coverageSelected,
  petType,
  powerup,
  setCoverageSelected = () => { },
}) => {
  const useCCProp103 = usePetcloudCCProp103;
  const store = useSelector(({ policies }) => ({ policies }), shallowEqual);
  const { changeCoverage } = store.policies;

  const multiModalData = useMemo(
    () => {
      if (useCCProp103) {
        // WIP prop103: only for dev
        return prop103.multiModalData;
      }

      return changeCoverage?.multimodalData.InsuranceMultiModalSectionDtoList
        ?.find((element) => element.Id === WELLNESS_MODAL_ID);
    },
    [changeCoverage],
  );

  const coverage = useMemo(() => {
    if (multiModalData && multiModalData
      .InsuranceMultiModalCategoryDtoList) {
      const wellnessType = WELLNESS_COVERAGE_LIMIT_FPI.BASIC
        .includes(bundleSelected?.CoverageLimitId)
        || WELLNESS_COVERAGE_LIMIT_IPG.BASIC
          .includes(bundleSelected?.CoverageLimitId)
        ? WELLNESS_MULTIMODAL_ID.BASIC
        : WELLNESS_MULTIMODAL_ID.PLUS;

      const multimodalDataList = multiModalData
        .InsuranceMultiModalCategoryDtoList
        .find((item) => item.Id === wellnessType);

      if (!multimodalDataList) {
        return {
          conclusion: {},
          items: [],
          title: {},
        };
      }

      const coverageList = restructureList(
        multimodalDataList,
        petType,
      );

      return {
        conclusion: coverageList[coverageList.length - 1],
        items: coverageList.slice(1, coverageList.length - 1),
        title: coverageList[0],
      };
    }
    return {
      conclusion: null,
      items: [],
      title: null,
    };
  }, [bundleSelected, multiModalData, petType]);

  const onTabChanged = useCallback((id) => {
    setCoverageSelected(id);
  }, [setCoverageSelected]);

  const tabList = useMemo(() => powerup.BundleInsuranceModifiers
    .map((item) => ({
      ...item,
      controls: 'coverage-details',
      id: item.Id,
    })), [powerup]);

  return (
    <div
      className={styles.container}
      data-selected={powerup.IsSelected}
    >
      {powerup?.BundleInsuranceModifiers?.length > 1 && (
        <TabList
          containerClassName={styles.buttonsContainer}
          onTabChanged={onTabChanged}
          tabClassName={styles.bundleModifierButton}
          TabContentItem={TabCoverage}
          tabIdPrefix="powerup-wellness"
          tabList={tabList}
          tabSelected={coverageSelected}
        />
      )}

      <div
        aria-labelledby={`powerup-wellness-${coverageSelected}`}
        className={styles.table}
        id="coverage-details"
        role="tabpanel"
      >
        <CoverageItem
          image={coverage.title?.BulletIconUrl}
          isBold={coverage.title?.IsBold}
          label={coverage.title?.ItemLabel}
          tooltipItems={coverage.title?.tooltipItems}
          value={coverage.title?.Value}
        />

        <ul className={styles.list}>
          {coverage.items.map((item) => (
            <CoverageItem
              key={item.Id}
              image={item.BulletIconUrl}
              isBold={item.IsBold}
              isListItem
              label={item.ItemLabel}
              tooltipItems={item.tooltipItems}
              value={item.Value}
            />
          ))}
        </ul>

        <CoverageItem
          image={coverage.conclusion?.BulletIconUrl}
          isBold={coverage.conclusion?.IsBold}
          label={coverage.conclusion?.ItemLabel}
          tooltipItems={coverage.conclusion?.tooltipItems}
          value={coverage.conclusion?.Value}
        />
      </div>
    </div>
  );
};
